import { useCallback, useEffect, useState } from "react";

import { generatePath, Link, Route, Switch, useParams } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import fetcher_v2 from "@/api/fetcher_v2";
import ArrowRight from "@/assets/icons/arrow-right.svg";
import Layout from "@/containers/Layout";
import { FEATURE } from "@/features";
import withRequiredFeature from "@/hocs/withRequiredFeature";
import { ROUTES } from "@/routes_new";
import AccessControl from "@components/AccessControl";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import Container from "@components/Container";
import Rhythm from "@components/Rhythm";
import Spinner from "@components/Spinner";
import Typography from "@components/Typography";

import { config } from "../../config";
import { NewReportView } from "./NewReportView/NewReportView";
import { ReportBuilder } from "./ReportBuilder";
import { ReportList } from "./ReportList";

import styles from "./ReportsView.module.css";

const OverviewView = () => {
  const [reports, setReports] = useState(null);
  const [shouldReloadReports, setShouldReloadReports] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);

  const { orgUuid } = useParams();

  useEffect(() => {
    document.body.classList.toggle("overview", true);
    document.body.classList.toggle("report_builder", false);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!shouldReloadReports) {
      setReportsLoading(true);
    }
    fetcher_v2(`${config.API_URL}/reports/`)
      .then(({ data }) => {
        if (mounted) {
          setReports(data);
          setReportsLoading(false);
        }
      })
      .catch(() => mounted && setReportsLoading(false));

    return () => (mounted = false);
  }, [shouldReloadReports]);

  const processedReports = reports?.map((s) => {
    return {
      ...s,
      onClickEdit: () => {
        postTrackingData("Edit report", {});
      },
      onClickSubscribers: () => {
        postTrackingData("Edit report subscriptions", {});
      },
    };
  });

  const deleteReportFromList = useCallback((uuid) => {
    setReports((currentReports) =>
      currentReports?.filter((report) => report.uuid !== uuid)
    );
  }, []);

  return (
    <Layout>
      <Container>
        <Rhythm>
          <div className="flex justify-between flex-wrap">
            <Typography variant="h1">{L.REPORTS_MAIN_TITLE}</Typography>
            <AccessControl>
              <Link
                icon={ArrowRight}
                to={generatePath(ROUTES.REPORTS_NEW_ORGID, { orgUuid })}
                component={Button}
                iconPlacement="after"
                variant="secondary"
                onClick={() => {
                  postTrackingData("Click on new report template link", {
                    templateName: "blank",
                  });
                }}
              >
                {L.REPORTS_CREATE_BLANK_BUTTON}
              </Link>
            </AccessControl>
          </div>

          <AccessControl>
            <NewReportView className="mt-3" />
          </AccessControl>

          {reportsLoading ? (
            <div className={styles.center}>
              <Spinner />
            </div>
          ) : (
            <>
              {processedReports?.length ? (
                <ReportList
                  className="mt-3"
                  data={processedReports}
                  setShouldReloadReports={setShouldReloadReports}
                  deleteReportFromList={deleteReportFromList}
                />
              ) : null}
            </>
          )}
        </Rhythm>
      </Container>
    </Layout>
  );
};

{
  /** TODO: Split these views, move them to customers-react and move <Layout> into them */
}
const InnerReportsView = () => (
  <>
    <AllEarsTitle title={L.REPORTS_MAIN_TITLE} />

    <Switch>
      <Route exact path={ROUTES.REPORTS_ORGID} component={OverviewView} />
      <Route
        exact
        path={ROUTES.REPORTS_NEW_ORGID}
        render={(props) => (
          <Layout>
            <Rhythm>
              <Container>
                <ReportBuilder {...props} isNewReport={true} />
              </Container>
            </Rhythm>
          </Layout>
        )}
      />
      <Route
        exact
        path={ROUTES.REPORTS_EDIT_ORGID}
        render={(props) => (
          <Layout>
            <Rhythm>
              <Container>
                <ReportBuilder {...props} />
              </Container>
            </Rhythm>
          </Layout>
        )}
      />
      <Route
        exact
        path={ROUTES.REPORTS_EDIT_SUBSCRIPTIONS_ORGID}
        render={(props) => (
          <Layout>
            <Rhythm>
              <Container>
                <ReportBuilder {...props} showScheduleModalOnInit={true} />
              </Container>
            </Rhythm>
          </Layout>
        )}
      />
    </Switch>
  </>
);

export const ReportsView = withRequiredFeature(InnerReportsView, {
  feature: FEATURE.HAS_REPORT_PRODUCT,
});
